interface ResourceCardProps {
  resource: {
    name: string;
    description: string;
    type: string;
    source: string;
    accessMode: string;
    icon: string;
    color: string;
    category: string;
    link?: string;
    note?: string;
    version?: string;
    formats?: string[];
    languages?: string[];
    platforms?: string[];
  };
}

export default function ResourceCard({ resource }: ResourceCardProps) {
  const handleAction = () => {
    if (resource.link) {
      window.open(resource.link, '_blank', 'noopener,noreferrer');
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'PDF':
        return 'ri-file-pdf-line';
      case 'Web':
        return 'ri-global-line';
      case 'App':
        return 'ri-smartphone-line';
      case 'PPTX':
        return 'ri-slideshow-line';
      case 'Software':
        return 'ri-computer-line';
      default:
        return 'ri-file-line';
    }
  };

  return (
    <div className="group bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-200 hover:border-emerald-300 hover:-translate-y-1">
      {/* Header - Minimal with icon */}
      <div className="p-5 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:from-emerald-50 group-hover:to-emerald-100 transition-all duration-300">
              <i className={`${resource.icon} text-lg text-gray-600 group-hover:text-emerald-700 transition-colors duration-300`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-emerald-800 transition-colors duration-300" style={{ fontFamily: 'Noto Sans, system-ui, sans-serif' }}>
                {resource.name}
              </h3>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <i className={`${getTypeIcon(resource.type)}`}></i>
                <span>{resource.type}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 bg-gradient-to-b from-white to-gray-50/30">
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
          {resource.description}
        </p>

        {/* Version Badge */}
        {resource.version && (
          <div className="mb-3">
            <span className="inline-flex items-center space-x-1 px-2.5 py-1 bg-gradient-to-r from-gray-100 to-gray-50 text-gray-700 rounded-md text-xs font-medium border border-gray-200">
              <i className="ri-bookmark-line"></i>
              <span>{resource.version}</span>
            </span>
          </div>
        )}

        {/* Formats */}
        {resource.formats && resource.formats.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1.5">
            {resource.formats.map((format, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-gradient-to-r from-gray-50 to-white text-gray-600 rounded text-xs font-medium border border-gray-200">
                {format}
              </span>
            ))}
          </div>
        )}

        {/* Languages */}
        {resource.languages && resource.languages.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center space-x-1.5 mb-1.5">
              <i className="ri-translate-2 text-gray-400 text-xs"></i>
              <span className="text-xs font-medium text-gray-600">Languages:</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {resource.languages.map((lang, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-gradient-to-r from-gray-50 to-white text-gray-600 rounded text-xs font-medium border border-gray-200">
                  {lang}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Platforms */}
        {resource.platforms && resource.platforms.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center space-x-1.5 mb-1.5">
              <i className="ri-device-line text-gray-400 text-xs"></i>
              <span className="text-xs font-medium text-gray-600">Platforms:</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {resource.platforms.map((platform, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-gradient-to-r from-gray-50 to-white text-gray-600 rounded text-xs font-medium border border-gray-200 flex items-center space-x-1">
                  {platform === 'Android' && <i className="ri-android-line text-xs"></i>}
                  {platform === 'iOS' && <i className="ri-apple-line text-xs"></i>}
                  {platform === 'Web' && <i className="ri-global-line text-xs"></i>}
                  <span>{platform}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div className="flex flex-wrap gap-1.5 mb-3 text-xs text-gray-500">
          <span className="px-2 py-0.5 bg-gradient-to-r from-gray-50 to-white rounded border border-gray-200">
            {resource.source}
          </span>
          <span className={`px-2 py-0.5 rounded border ${
            resource.accessMode === 'Download' 
              ? 'bg-gradient-to-r from-emerald-50 to-emerald-100/50 text-emerald-700 border-emerald-200' 
              : 'bg-gradient-to-r from-gray-50 to-white text-gray-600 border-gray-200'
          }`}>
            {resource.accessMode}
          </span>
        </div>

        {/* Note */}
        {resource.note && (
          <div className="mb-4 p-2.5 bg-gradient-to-r from-amber-50 to-amber-100/30 border border-amber-200 rounded-md">
            <div className="flex items-start space-x-1.5">
              <i className="ri-information-line text-amber-600 text-xs mt-0.5"></i>
              <p className="text-xs text-amber-800 leading-relaxed">
                {resource.note}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Action Area - Minimal, right-aligned with hover effect */}
      <div className="px-5 py-3 bg-gradient-to-r from-gray-50 to-white border-t border-gray-100 group-hover:from-emerald-50 group-hover:to-white transition-all duration-300">
        <button
          onClick={handleAction}
          className="ml-auto flex items-center space-x-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-800 transition-colors cursor-pointer whitespace-nowrap group-hover:scale-105 transform duration-200"
        >
          {resource.accessMode === 'Download' ? (
            <>
              <span>Download</span>
              <i className="ri-download-line text-base group-hover:animate-bounce"></i>
            </>
          ) : resource.type === 'App' ? (
            <>
              <span>Get App</span>
              <i className="ri-smartphone-line text-base"></i>
            </>
          ) : (
            <>
              <span>Open</span>
              <i className="ri-external-link-line text-base"></i>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
